import Box from '@mui/material/Box';
import { KmxContainer } from '../../../components/KmxContainer';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import useAppState from '../../../common/state/appState';
import { useEffect, useState, useMemo } from 'react';
import { IFBSDecision } from '../../../api/types/finance';
import { useMuiMediaQuery } from '../../../utilities/hooks';
import { ANALYTICS_CONSTANTS, trackAnchorClick } from '../../../utilities/analytics';
import { carTileFinanceHelper, CarTile as CarTileFuture } from '@kmx/car-tile-future';
import type { CarTileFbsInformation, SmallBatchApplication, SmallBatchLenderOffer } from './types/carTileFbsTypes';
import mapVehicleToCar from './utilities/mapVehicleToCar';
import { AppointmentTypes } from '../utilities/constants';
import { ActionsConfig } from './types/ActionsConfig';
import usePageStateTracking from '../utilities/pageStateStatusHelpers';

const UFTTransferStatus = () => {
    const transferStatus = useAppState((state) => state.transferStatus);
    const isTestDrive = transferStatus?.appointmentType
        ?.toLowerCase()
        .includes(AppointmentTypes.TEST_DRIVE.toLowerCase());
    const car = useMemo(() => transferStatus && mapVehicleToCar(transferStatus), [transferStatus]);
    const [fbsDecision, setFbsDecision] = useState<IFBSDecision>();
    const { isDesktop } = useMuiMediaQuery();
    const [isFavoriteInUI, setIsFavoriteInUI] = useState<boolean>(false);
    const favoriteVehicles = useAppState((state) => state.favoriteVehicles);
    const addVehicleToFavorites = useAppState((state) => state.addVehicleToFavorites);
    const removeVehicleFromFavorites = useAppState((state) => state.removeVehicleFromFavorites);
    const getFbsDataForStockNumbers = useAppState((state) => state.getFbsDataForStockNumbers);
    const fbsData = useAppState((state) => state.fbsData);

    const monthlyPaymentConfig = {
        amount: fbsDecision?.fbsDecision?.terms?.[0]?.minTermRate?.monthlyPayment,
        isEstimated: true,
        onClick: () => {},
    };

    useEffect(() => {
        if (fbsDecision) return;
        const decision = fbsData.find((data) => data.stockNumber.toString() === car?.stockNumber);
        setFbsDecision(decision);
    }, [car, fbsData, fbsDecision]);

    useEffect(() => {
        if (!car) return;
        setIsFavoriteInUI(favoriteVehicles.includes(Number(car.stockNumber)));
    }, [favoriteVehicles, car]);

    usePageStateTracking();

    const handleHeartClick = () => {
        if (!car) return;

        const stockNumber = Number(car.stockNumber);
        setIsFavoriteInUI(!isFavoriteInUI);

        if (isFavoriteInUI) {
            removeVehicleFromFavorites(stockNumber);
        } else {
            addVehicleToFavorites(stockNumber);
        }
    };

    useEffect(() => {
        if (!car || fbsDecision) return;
        getFbsDataForStockNumbers([parseInt(car.stockNumber)]);
    }, [car, fbsDecision, getFbsDataForStockNumbers]);

    if (!transferStatus || !car || !transferStatus.status) {
        return <></>;
    }

    const viewDetailsHref = `/car/${car.stockNumber}`;
    const actions: ActionsConfig = isTestDrive
        ? {
              primary: {
                  children: 'Edit appointment',
                  variant: 'contained',
                  onClick: (e) =>
                      trackAnchorClick({
                          clickEvent: e,
                          taggingEvent: ANALYTICS_CONSTANTS.EVENTS.CLICK.CTA,
                          title: 'set my appointment',
                          position: 'Homepage',
                          href: '/hub/dashboard',
                          page: 'Homepage',
                      }),
              },
              secondary: {
                  children: 'View details',
                  variant: 'outlined',
                  onClick: (e) =>
                      trackAnchorClick({
                          clickEvent: e,
                          taggingEvent: ANALYTICS_CONSTANTS.EVENTS.CLICK.CTA,
                          title: 'view details',
                          position: 'Homepage',
                          href: viewDetailsHref,
                          page: 'Homepage',
                      }),
              },
          }
        : {
              primary: {
                  children: 'View details',
                  variant: 'outlined',
                  onClick: (e) =>
                      trackAnchorClick({
                          clickEvent: e,
                          taggingEvent: ANALYTICS_CONSTANTS.EVENTS.CLICK.CTA,
                          title: 'view details',
                          position: 'Homepage',
                          href: viewDetailsHref,
                          page: 'Homepage',
                      }),
              },
              secondary: {
                  children: 'Track order',
                  variant: 'contained',
                  onClick: (e) =>
                      trackAnchorClick({
                          clickEvent: e,
                          taggingEvent: ANALYTICS_CONSTANTS.EVENTS.CLICK.CTA,
                          title: 'track order',
                          position: 'Homepage',
                          href: '/hub/dashboard',
                          page: 'Homepage',
                      }),
              },
          };

    return (
        <Box
            sx={{
                position: 'relative',
                backgroundImage: {
                    xs: 'url(/images/home/hero/transfer-status/actionhero-xs.jpg)',
                    sm: 'url(/images/home/hero/transfer-status/actionhero-sm.jpg)',
                    md: 'url(/images/home/hero/transfer-status/actionhero-med.jpg)',
                },
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat',
            }}
        >
            <KmxContainer
                maxWidth="xl"
                sx={{
                    py: { xs: 1.5, sm: 4 },
                    mt: { xs: 1.5, sm: 4 },
                }}
            >
                <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    spacing={{ xs: 3, sm: 4, md: 8 }}
                    sx={{
                        height: '100%',
                        py: { xs: 5, sm: 6 },
                        position: 'relative',
                        zIndex: 1,
                        alignItems: { xs: 'center', sm: 'flex-start' },
                        justifyContent: 'center',
                    }}
                >
                    <Typography
                        component="h2"
                        sx={{
                            typography: { xs: 'h4', sm: 'h3' },
                            whiteSpace: { xs: 'normal', sm: 'pre-line' },
                            flexShrink: 0,
                        }}
                    >
                        {`You're one
                            step closer
                            to the one!`}
                    </Typography>
                    <Box sx={{ width: '100%', position: 'relative' }}>
                        <CarTileFuture
                            availability={car.location.transferText}
                            imageSrc={`https://img2.carmax.com/assets/${car.stockNumber}/hero.jpg?width=800&height=600`}
                            car={car}
                            monthlyPayment={monthlyPaymentConfig}
                            fbsInformation={buildFbsInformation(car.price, fbsDecision)}
                            isFeatured
                            size={isDesktop ? 'xlarge' : 'large'}
                            favorites={{
                                isFavorited: isFavoriteInUI,
                                onChange: handleHeartClick,
                            }}
                            actions={actions}
                        />
                    </Box>
                </Stack>
            </KmxContainer>
        </Box>
    );
};

export default UFTTransferStatus;

const buildFbsInformation = (price: number, fbsDecision?: IFBSDecision): CarTileFbsInformation | undefined => {
    if (!fbsDecision || !fbsDecision.fbsDecision) return undefined;
    const {
        decision,
        downPayment,
        stateAbbreviation,
        tradeInBreakdown,
        totalFinanceAmount,
        conditionAmount,
        lenderName,
        taxesAndFeesRounded,
        terms,
    } = fbsDecision.fbsDecision;

    const lenderOffer: SmallBatchLenderOffer = {
        offerStatus: decision,
        totalFinanceAmount,
        conditionAmount,
        lenderName,
        taxesAndFees: taxesAndFeesRounded,
        terms,
    };
    const financeApplication: SmallBatchApplication = {
        currentDownPayment: downPayment,
        applicationStoreState: stateAbbreviation,
        tradeInBreakdown,
    };

    const result: CarTileFbsInformation = carTileFinanceHelper(price, lenderOffer, financeApplication);

    return result;
};
